import React, { useState, useEffect } from 'react';
import { useAuth, useTheme } from '../App';
import { 
  Settings as SettingsIcon, Key, Bell, Users, Shield, Database,
  Save, Eye, EyeOff, Loader2, CheckCircle, AlertCircle, RefreshCw,
  Brain, MessageCircle, Globe, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
  const { token, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('integrations');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showKeys, setShowKeys] = useState({
    openai: false,
    twilio_sid: false,
    twilio_token: false
  });

  // Integration settings
  const [integrations, setIntegrations] = useState({
    openai_api_key: '',
    twilio_account_sid: '',
    twilio_auth_token: '',
    twilio_phone_number: '',
    whatsapp_enabled: true,
    ai_auto_response: true,
    ai_confidence_threshold: 85
  });

  // Connection status
  const [connectionStatus, setConnectionStatus] = useState({
    openai: 'unknown',
    twilio: 'unknown'
  });

  useEffect(() => {
    fetchSettings();
  }, [token]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/support/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.integrations) {
          setIntegrations(prev => ({ ...prev, ...data.integrations }));
        }
        if (data.connectionStatus) {
          setConnectionStatus(data.connectionStatus);
        }
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveIntegrations = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/api/support/settings/integrations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(integrations)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Integration settings saved successfully');
        if (data.connectionStatus) {
          setConnectionStatus(data.connectionStatus);
        }
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to save settings');
      }
    } catch (error) {
      toast.error('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (service) => {
    setConnectionStatus(prev => ({ ...prev, [service]: 'testing' }));
    try {
      const response = await fetch(`${API}/api/support/settings/test-connection/${service}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(integrations)
      });

      if (response.ok) {
        setConnectionStatus(prev => ({ ...prev, [service]: 'connected' }));
        toast.success(`${service.charAt(0).toUpperCase() + service.slice(1)} connection successful`);
      } else {
        setConnectionStatus(prev => ({ ...prev, [service]: 'error' }));
        toast.error(`${service.charAt(0).toUpperCase() + service.slice(1)} connection failed`);
      }
    } catch (error) {
      setConnectionStatus(prev => ({ ...prev, [service]: 'error' }));
      toast.error(`Failed to test ${service} connection`);
    }
  };

  const getConnectionBadge = (status) => {
    const badges = {
      'connected': { class: 'bg-emerald-500/20 text-emerald-400', icon: CheckCircle, text: 'Connected' },
      'error': { class: 'bg-red-500/20 text-red-400', icon: AlertCircle, text: 'Error' },
      'testing': { class: 'bg-blue-500/20 text-blue-400', icon: RefreshCw, text: 'Testing...' },
      'unknown': { class: 'bg-zinc-500/20 text-zinc-400', icon: AlertCircle, text: 'Not Configured' },
    };
    const badge = badges[status] || badges.unknown;
    return (
      <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${badge.class}`}>
        <badge.icon className={`w-3 h-3 ${status === 'testing' ? 'animate-spin' : ''}`} />
        {badge.text}
      </span>
    );
  };

  const tabs = [
    { id: 'integrations', label: 'Integrations', icon: Key },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'team', label: 'Team', icon: Users },
    { id: 'security', label: 'Security', icon: Shield },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <SettingsIcon className="w-6 h-6 text-primary" />
          Settings
        </h1>
        <p className="text-muted-foreground">Configure your support automation system</p>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Sidebar Tabs */}
        <div className="lg:col-span-1">
          <div className="support-card p-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === tab.id 
                    ? 'bg-primary text-primary-foreground' 
                    : 'hover:bg-secondary text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-5 h-5" />
                <span className="font-medium">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-3 space-y-6">
          {activeTab === 'integrations' && (
            <>
              {/* OpenAI Integration */}
              <div className="support-card">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <Brain className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold">OpenAI Integration</h3>
                      <p className="text-sm text-muted-foreground">AI-powered ticket summaries and auto-responses</p>
                    </div>
                  </div>
                  {getConnectionBadge(connectionStatus.openai)}
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">API Key *</label>
                    <div className="relative">
                      <input
                        type={showKeys.openai ? 'text' : 'password'}
                        value={integrations.openai_api_key}
                        onChange={(e) => setIntegrations({...integrations, openai_api_key: e.target.value})}
                        placeholder="sk-..."
                        className="support-input pr-20"
                        data-testid="openai-api-key"
                      />
                      <button
                        onClick={() => setShowKeys({...showKeys, openai: !showKeys.openai})}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-secondary rounded"
                      >
                        {showKeys.openai ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Get your API key from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">OpenAI Dashboard</a>
                    </p>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">AI Auto-Response</p>
                      <p className="text-xs text-muted-foreground">Automatically respond to common queries</p>
                    </div>
                    <button
                      onClick={() => setIntegrations({...integrations, ai_auto_response: !integrations.ai_auto_response})}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        integrations.ai_auto_response ? 'bg-primary' : 'bg-secondary'
                      }`}
                    >
                      <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                        integrations.ai_auto_response ? 'right-1' : 'left-1'
                      }`}></span>
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">AI Confidence Threshold: {integrations.ai_confidence_threshold}%</label>
                    <input
                      type="range"
                      min="50"
                      max="100"
                      value={integrations.ai_confidence_threshold}
                      onChange={(e) => setIntegrations({...integrations, ai_confidence_threshold: parseInt(e.target.value)})}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Escalate to human agent if AI confidence is below this threshold
                    </p>
                  </div>

                  <button
                    onClick={() => testConnection('openai')}
                    disabled={!integrations.openai_api_key || connectionStatus.openai === 'testing'}
                    className="support-btn-secondary flex items-center gap-2"
                    data-testid="test-openai-btn"
                  >
                    <Zap className="w-4 h-4" />
                    Test Connection
                  </button>
                </div>
              </div>

              {/* Twilio Integration */}
              <div className="support-card">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                      <MessageCircle className="w-5 h-5 text-red-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Twilio Integration</h3>
                      <p className="text-sm text-muted-foreground">SMS and WhatsApp messaging</p>
                    </div>
                  </div>
                  {getConnectionBadge(connectionStatus.twilio)}
                </div>

                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Account SID *</label>
                      <div className="relative">
                        <input
                          type={showKeys.twilio_sid ? 'text' : 'password'}
                          value={integrations.twilio_account_sid}
                          onChange={(e) => setIntegrations({...integrations, twilio_account_sid: e.target.value})}
                          placeholder="AC..."
                          className="support-input pr-10"
                          data-testid="twilio-sid"
                        />
                        <button
                          onClick={() => setShowKeys({...showKeys, twilio_sid: !showKeys.twilio_sid})}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-secondary rounded"
                        >
                          {showKeys.twilio_sid ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Auth Token *</label>
                      <div className="relative">
                        <input
                          type={showKeys.twilio_token ? 'text' : 'password'}
                          value={integrations.twilio_auth_token}
                          onChange={(e) => setIntegrations({...integrations, twilio_auth_token: e.target.value})}
                          placeholder="Your auth token"
                          className="support-input pr-10"
                          data-testid="twilio-token"
                        />
                        <button
                          onClick={() => setShowKeys({...showKeys, twilio_token: !showKeys.twilio_token})}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-secondary rounded"
                        >
                          {showKeys.twilio_token ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Twilio Phone Number</label>
                    <input
                      type="text"
                      value={integrations.twilio_phone_number}
                      onChange={(e) => setIntegrations({...integrations, twilio_phone_number: e.target.value})}
                      placeholder="+1234567890"
                      className="support-input"
                      data-testid="twilio-phone"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Get your credentials from <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Twilio Console</a>
                    </p>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">WhatsApp Support</p>
                      <p className="text-xs text-muted-foreground">Enable WhatsApp messaging channel</p>
                    </div>
                    <button
                      onClick={() => setIntegrations({...integrations, whatsapp_enabled: !integrations.whatsapp_enabled})}
                      className={`relative w-12 h-6 rounded-full transition-colors ${
                        integrations.whatsapp_enabled ? 'bg-primary' : 'bg-secondary'
                      }`}
                    >
                      <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                        integrations.whatsapp_enabled ? 'right-1' : 'left-1'
                      }`}></span>
                    </button>
                  </div>

                  <button
                    onClick={() => testConnection('twilio')}
                    disabled={!integrations.twilio_account_sid || !integrations.twilio_auth_token || connectionStatus.twilio === 'testing'}
                    className="support-btn-secondary flex items-center gap-2"
                    data-testid="test-twilio-btn"
                  >
                    <Zap className="w-4 h-4" />
                    Test Connection
                  </button>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end">
                <button
                  onClick={handleSaveIntegrations}
                  disabled={saving}
                  className="support-btn-primary flex items-center gap-2 px-6"
                  data-testid="save-integrations-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Integration Settings
                </button>
              </div>
            </>
          )}

          {activeTab === 'notifications' && (
            <div className="support-card">
              <h3 className="font-semibold mb-4">Notification Preferences</h3>
              <div className="space-y-4">
                {[
                  { label: 'New Ticket Alerts', desc: 'Get notified when a new ticket is created' },
                  { label: 'Escalation Alerts', desc: 'Get notified when a ticket is escalated' },
                  { label: 'SLA Breach Warnings', desc: 'Get notified before SLA breach' },
                  { label: 'Daily Summary', desc: 'Receive daily summary of support metrics' },
                  { label: 'AI Handoff Alerts', desc: 'Get notified when AI hands off to human' },
                ].map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">{item.label}</p>
                      <p className="text-xs text-muted-foreground">{item.desc}</p>
                    </div>
                    <button className="relative w-12 h-6 bg-primary rounded-full">
                      <span className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full"></span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'team' && (
            <div className="support-card">
              <h3 className="font-semibold mb-4">Team Management</h3>
              <p className="text-muted-foreground text-sm mb-4">Manage your support team members and their roles</p>
              <div className="space-y-3">
                {[
                  { name: 'Sarah Mitchell', email: 'sarah@company.com', role: 'Admin' },
                  { name: 'Mike Thompson', email: 'mike@company.com', role: 'Agent' },
                  { name: 'John Davis', email: 'john@company.com', role: 'Agent' },
                ].map((member, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-medium">
                        {member.name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{member.name}</p>
                        <p className="text-xs text-muted-foreground">{member.email}</p>
                      </div>
                    </div>
                    <span className="text-xs bg-secondary px-2 py-1 rounded">{member.role}</span>
                  </div>
                ))}
              </div>
              <button className="support-btn-primary mt-4">
                Invite Team Member
              </button>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="support-card">
              <h3 className="font-semibold mb-4">Security Settings</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Two-Factor Authentication</label>
                  <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Enable 2FA</p>
                      <p className="text-xs text-muted-foreground">Add an extra layer of security</p>
                    </div>
                    <button className="relative w-12 h-6 bg-secondary rounded-full">
                      <span className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full"></span>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Session Timeout</label>
                  <select className="support-input">
                    <option value="30">30 minutes</option>
                    <option value="60">1 hour</option>
                    <option value="120">2 hours</option>
                    <option value="480">8 hours</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">API Access</label>
                  <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Enable API Access</p>
                      <p className="text-xs text-muted-foreground">Allow external API connections</p>
                    </div>
                    <button className="relative w-12 h-6 bg-primary rounded-full">
                      <span className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full"></span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
